import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    company_name: "",
    account_type: "individual",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await register(form);
    setSubmitting(false);
    if (res.ok) {
      navigate("/app");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-emerald-950 text-white relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/508d46d6-a5df-4630-9d10-2e40da390746/images/7a288df1257c0d28acc8230c4974846117fb89c9e37e36628daeb0249265bffd.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <Link to="/" className="relative flex items-center gap-2.5" data-testid="register-logo-link">
          <div className="h-11 w-11 rounded-lg bg-white flex items-center justify-center p-1">
            <img src="/logo.png" alt="PRC会計" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">PRC会計</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-emerald-200">SaaS</div>
          </div>
        </Link>
        <div className="relative space-y-6 max-w-md">
          <h2 className="font-display text-3xl xl:text-4xl leading-tight tracking-tight">
            14日間、すべての機能を<br />無料でお試しください。
          </h2>
          <p className="text-sm text-emerald-100 leading-relaxed">
            クレジットカード不要。1分で登録完了。
            あなたの事業のための会計プラットフォームを今すぐ体験。
          </p>
          <ul className="space-y-2.5 text-sm text-emerald-100">
            <li>✓ 請求書 無制限発行</li>
            <li>✓ 経費・仕訳管理</li>
            <li>✓ P/L・B/S・キャッシュフロー</li>
          </ul>
        </div>
        <div className="relative text-xs text-emerald-200">© 2026 PRC会計 SaaS</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            data-testid="register-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 mb-8"
          >
            <ArrowLeft className="h-4 w-4" /> ホームに戻る
          </Link>

          <h1 className="font-display font-bold text-3xl text-stone-900">アカウントを作成</h1>
          <p className="mt-2 text-sm text-stone-500">事業情報を入力して、はじめてください。</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="register-form">
            {error && (
              <div
                data-testid="register-error"
                className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700"
              >
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="name" className="text-stone-700 text-sm">お名前</Label>
              <Input
                id="name"
                value={form.name}
                onChange={update("name")}
                required
                data-testid="register-name-input"
                className="mt-1.5"
                placeholder="山田 太郎"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-stone-700 text-sm">メールアドレス</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                data-testid="register-email-input"
                className="mt-1.5"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-stone-700 text-sm">パスワード</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={update("password")}
                required
                minLength={6}
                data-testid="register-password-input"
                className="mt-1.5"
                placeholder="6文字以上"
              />
            </div>
            <div>
              <Label htmlFor="company_name" className="text-stone-700 text-sm">屋号・会社名（任意）</Label>
              <Input
                id="company_name"
                value={form.company_name}
                onChange={update("company_name")}
                data-testid="register-company-input"
                className="mt-1.5"
                placeholder="株式会社○○"
              />
            </div>
            <div>
              <Label className="text-stone-700 text-sm">事業形態</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {[
                  { v: "individual", label: "個人事業主" },
                  { v: "sme", label: "法人・中小企業" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`register-account-type-${opt.v}`}
                    onClick={() => setForm({ ...form, account_type: opt.v })}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      form.account_type === opt.v
                        ? "border-emerald-900 bg-emerald-50 text-emerald-900"
                        : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit-button"
              className="w-full inline-flex items-center justify-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-950 transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "アカウントを作成"}
            </button>
          </form>

          <p className="mt-6 text-sm text-stone-500">
            すでにアカウントをお持ちの方は{" "}
            <Link to="/login" data-testid="register-login-link" className="text-emerald-800 font-medium link-underline">
              ログイン
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
